import { BuyCheckoutState } from '@buy/state/checkout/state'
import { getCheckoutState, getPlanData } from '@buy/state/selectors'
import { BuyPlan, PriceInfo } from '@buy/state/plan'
import { select, call, SelectEffect, CallEffect } from 'redux-saga/effects'
import { getCurrentAddons } from '@buy/utils/get-current-addons'
import { BuyAddon } from '@buy/state/add-on'
import { has, isEmpty } from 'lodash-es'
import buyServices from '@buy/services'
import { PriceInfoPayload } from '@buy/state/user/state'
import { PriceInfoPeriod } from '@common/constants'
import { GenericResponse } from '@common/state/generic-response/generic-response'
import { BuyPrice } from '@buy/state/price'

export function* getSelectedAddons(): Generator<
    SelectEffect | CallEffect<BuyPlan> | CallEffect<BuyAddon[]>,
    BuyAddon[],
    BuyCheckoutState & BuyPlan & BuyAddon[]
> {
    const { selectedAddons }: BuyCheckoutState = yield select(getCheckoutState)
    const selectedPlan: BuyPlan = yield select(getPlanData)
    const myAddons: BuyAddon[] = yield call(getCurrentAddons, selectedAddons, selectedPlan)
    return myAddons
}

export function* getPricingInfo(plan: BuyPlan, currency: string[]) {
    const skuList = new Set()
    skuList.add(plan.sKU)
    plan.sKUSelectors?.forEach((item) => {
        skuList.add(item.sku)
    })
    plan.addons?.forEach((addon) => {
        if (has(addon, 'sKUSelectors')) skuList.add(addon.sKU)
    })

    const payload: PriceInfoPayload = { productSKUs: Array.from(skuList).join(','), currency: currency.join(',') }
    const priceInfoResponse: GenericResponse = yield call(buyServices.getPricingInfoData, payload)
    return priceInfoResponse.data
}

const remapPricingInfo = (pricingInfo: BuyPrice, quantityMultiplier?: string): BuyPrice => {
    const countriesPricingMap = new Map()
    Object.keys(pricingInfo).forEach((countryKey: string) => {
        const billingTenurePricing = new Map()
        Object.keys(pricingInfo[countryKey]).forEach((tenureKey: string) => {
            const tiersMap = new Map()
            Object.keys(pricingInfo[countryKey][tenureKey]).forEach((tierKey: string) => {
                const newPriceItem = { ...pricingInfo[countryKey][tenureKey][tierKey] }
                if (tenureKey === 'UpfrontAnnual') {
                    newPriceItem.price = newPriceItem.monthlyUnitPrice
                }
                if (!isEmpty(quantityMultiplier)) {
                    newPriceItem.price *= Number(quantityMultiplier)
                    newPriceItem.min /= Number(quantityMultiplier)
                    newPriceItem.max /= Number(quantityMultiplier)
                }
                tiersMap.set(tierKey, newPriceItem)
            })
            billingTenurePricing.set(PriceInfoPeriod[tenureKey], Object.fromEntries(tiersMap))
        })
        countriesPricingMap.set(countryKey, Object.fromEntries(billingTenurePricing))
    })
    const result = Object.fromEntries(countriesPricingMap) as BuyPrice
    return result
}

export function mapPricingInfo(plan: BuyPlan, skuPricingInfo: PriceInfo[]) {
    const baseSku = skuPricingInfo.find((x) => x.productSKU.toLowerCase() === plan.sKU.toLowerCase())
    if (!isEmpty(baseSku)) {
        plan.pricingInfo = remapPricingInfo(baseSku.pricingInfo) // eslint-disable-line no-param-reassign
    }

    plan.addons?.forEach((addon) => {
        const addonSku = skuPricingInfo.find((x) => x.productSKU.toLowerCase() === addon.sKU.toLowerCase())
        if (!isEmpty(addonSku)) {
            const reMappedAddonSkuPricingInfo = remapPricingInfo(
                addonSku.pricingInfo,
                addon.sKUSelector?.quantityMultiplier
            )
            addon.pricingInfo = reMappedAddonSkuPricingInfo // eslint-disable-line no-param-reassign
            if (!isEmpty(addon.sKUSelector)) addon.sKUSelector.pricingInfo = { ...reMappedAddonSkuPricingInfo } // eslint-disable-line no-param-reassign
        }
    })
    plan.sKUSelectors?.forEach((item) => {
        const skuSelectorSku = skuPricingInfo.find((x) => x.productSKU.toLowerCase() === item.sku.toLowerCase())
        if (!isEmpty(skuSelectorSku)) {
            item.pricingInfo = remapPricingInfo(skuSelectorSku.pricingInfo, item.quantityMultiplier) // eslint-disable-line no-param-reassign
        }
    })
    return plan
}
